import {
  AssignmentOutlined as AssignmentOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  KeyboardDoubleArrowLeftOutlined,
} from "@mui/icons-material"
import { getMeData } from "app/homepage/slice"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { setMobileMenuOpen } from "../../app/homepage/menuSlice"
import { useGetReviewsQuery } from "../../pages/reviews/ReviewsSlice"
import { fetchSettings } from "../../pages/settings/settingsSlice"
import { useGetSubscriptionStatusQuery } from "../../pages/subscription/subscriptionApi"

const isMobile = window.screen.width < 500

export function MobileSidebar({ pathList, toggleNavbar }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { step: reduxStep } = useSelector((state) => state.StepSliceReducer)
  const { isMobileMenuOpen } = useSelector((state) => state.menuSliceReducer)
  const user = useSelector((user) => user.main.user)
  const { list: settingsList } = useSelector(
    (state) => state.settingsSliceReducer
  )

  // Получаем step из Redux или из user объекта как fallback, или из localStorage
  const step = useMemo(() => {
    if (reduxStep) return reduxStep
    if (user?.step) return user.step
    try {
      const userData = localStorage.getItem("user")
      if (userData) {
        const localUser = JSON.parse(userData)
        return localUser?.step || ""
      }
    } catch {
      // ignore
    }
    return ""
  }, [reduxStep, user?.step])
  const { data: reviewsData } = useGetReviewsQuery({
    perPage: 10000,
    page: 1,
    limit: 10000,
  })

  const dispatch = useDispatch()

  // Убираем старую логику подсказок, теперь используем HandPointerHint

  function navigateTo(path) {
    navigate(path)
    setTimeout(() => {
      dispatch(setMobileMenuOpen(false))
    }, 300)
  }

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken")
    if (accessToken) {
      dispatch(getMeData())
    }
  }, [dispatch])

  // Загружаем настройки для получения direction_id
  useEffect(() => {
    // Используем user из Redux, если он есть, иначе из localStorage
    const currentUser =
      user ||
      (() => {
        try {
          const userData = localStorage.getItem("user")
          return userData ? JSON.parse(userData) : {}
        } catch {
          return {}
        }
      })()

    const salon_id = currentUser?.salon_id || currentUser?.restaurant_id
    if (
      salon_id &&
      (!settingsList?.direction_id || settingsList?.direction_id === 0)
    ) {
      dispatch(fetchSettings(String(salon_id)))
    }
  }, [dispatch, settingsList?.direction_id, user])

  const [expandedMenus, setExpandedMenus] = useState({})

  // Получаем статус подписки
  const { data: subscriptionData, isLoading: isSubscriptionLoading } = useGetSubscriptionStatusQuery()

  // Проверяем, активна ли подписка PRO (используем useMemo для пересчета при изменении subscriptionData)
  const isProActive = useMemo(() => {
    // Пока данные загружаются — считаем Pro активным (оптимистично),
    // чтобы Лаборатория не мигала при первой загрузке страницы
    if (isSubscriptionLoading) return true
    if (!subscriptionData) return false
    if (subscriptionData.status === "none") return false

    const isPro = subscriptionData.plan?.toLowerCase() === "pro"
    const isApiActive =
      subscriptionData.is_active || subscriptionData.status === "active"
    const daysLeft = subscriptionData.days_left || 0
    const isTimeExpired = daysLeft <= 0

    return isPro && isApiActive && !isTimeExpired
  }, [subscriptionData, isSubscriptionLoading])

  const isAdmin = user?.admin
  const isAdministratorRole = user?.roles?.some((role) => role.id === 2)

  const ADMIN_VISIBLE_MENU = [
    "Расписание",
    "Обратная связь",
    "Финансы",
    "Справочники",
  ]

  // Получаем direction_id_from_user с пересчетом при изменении user
  const direction_id_from_user = useMemo(() => {
    try {
      const stored = localStorage.getItem("direction_id")
      if (stored) {
        return JSON.parse(stored)
      }
    } catch {
      // ignore
    }
    return user?.restaurant?.direction_id
  }, [user?.restaurant?.direction_id])

  const userRoleId =
    JSON.parse(localStorage.getItem("userRoleId")) || user?.roles?.[0]?.id

  // Используем direction_id из settingsList с fallback на direction_id_from_user
  const direction_id = useMemo(() => {
    return (
      settingsList?.direction_id ||
      direction_id_from_user ||
      user?.restaurant?.direction_id ||
      user?.salon?.direction_id
    )
  }, [
    settingsList?.direction_id,
    direction_id_from_user,
    user?.restaurant?.direction_id,
    user?.salon?.direction_id,
  ])

  // Вычисляем, должна ли отображаться вкладка "Лаборатория"
  const shouldShowLab = useMemo(() => {
    return direction_id === 2 && step && Number(step) >= 4 && isProActive
  }, [direction_id, step, isProActive])

  const toggleSubmenu = (e, value) => {
    e.stopPropagation()
    setExpandedMenus(prev => ({
      ...prev,
      [value]: !prev[value]
    }))
  }

  return (
    <>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`${
          !isMobileMenuOpen ? "translate-x-[-100%]" : "translate-x-0"
        } transition-transform duration-300 sm:hidden  flex fixed top-[60px] left-0 bottom-0 z-[100] MobileSidebar`}
      >
        <div className='h-full bg-blue pt-5 px-6 [&>a]:mb-5 w-fit z-10 overflow-y-auto no-scrollbar max-h-screen pr-6'>
          <div className='flex items-center justify-between'>
            <div className='w-fit h-fit flex text-orange sm:text-2xl text-xl my-7'>
              <img src='/assets/logo/logo.svg' alt='Logo' />
            </div>
            <button
              onClick={() => {
                dispatch(setMobileMenuOpen(false))
              }}
              className='text-white hover:text-orange h-fit [&>svg]:w-[20px]'
            >
              <KeyboardDoubleArrowLeftOutlined className='w-[20px] text-orange' />
            </button>
          </div>

          {pathList.map(({ path, value, Icon, hasSubmenu, submenu }, index) => {
            const valueTrimmed = value.trim()

            // Администратор (roles id 2) — даже при admin === "0"
            if (isAdministratorRole) {
              if (!ADMIN_VISIBLE_MENU.includes(valueTrimmed)) return null

              if (hasSubmenu) {
                return (
                  <React.Fragment key={path}>
                    <div
                      onClick={(e) => toggleSubmenu(e, valueTrimmed)}
                      className={`flex items-center w-full whitespace-nowrap mb-7 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group cursor-pointer ${
                        pathname.startsWith(path)
                          ? "text-orange [&>div]:text-orange"
                          : "text-white"
                      }`}
                    >
                      <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon
                          sx={{
                            width: "20px",
                            color: pathname.startsWith(path) ? "secondary.main" : undefined,
                          }}
                        />
                      </div>
                      <p className=' w-fit pr-[10px]'>{value}</p>
                      <div className='text-white hover:text-orange'>
                        {expandedMenus[valueTrimmed] ? (
                          <KeyboardArrowDownIcon />
                        ) : (
                          <KeyboardArrowRightIcon />
                        )}
                      </div>
                    </div>
                    {expandedMenus[valueTrimmed] && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className='ml-10 flex flex-col mb-4'
                      >
                        {submenu.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => {
                              dispatch(setMobileMenuOpen(false))
                            }}
                            className={`${
                              pathname === subItem.path
                                ? "text-orange [&>div]:text-orange"
                                : "text-white"
                            } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group`}
                          >
                            <span className=' w-fit pr-[10px]'>
                              {subItem.value}
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                )
              }

              return (
                <div key={path}>
                  <button
                    onClick={() => navigateTo(path)}
                    className={`${
                      pathname === path
                        ? "text-orange [&>div]:text-orange"
                        : "hover:text-orange [&>div]:text-gray group text-white"
                    } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                  >
                    <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon
                        sx={{
                          width: "20px",
                          color: pathname === path ? "secondary.main" : undefined,
                        }}
                      />
                    </div>
                    <span className='w-fit'>{value}</span>
                  </button>
                </div>
              )
            }

            // Мастер: admin === "0" без роли администратора
            if (isAdmin === "0") {
              if (!["Обратная связь", "Расписание"].includes(valueTrimmed)) {
                return null
              }
              return (
                <div key={path}>
                  <button
                    onClick={() => navigateTo(path)}
                    className={`${
                      pathname === path
                        ? "text-orange [&>div]:text-orange"
                        : "hover:text-orange [&>div]:text-gray group text-white"
                    } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                  >
                    <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon
                        sx={{
                          width: "20px",
                          color:
                            pathname === path ? "secondary.main" : undefined,
                        }}
                      />
                    </div>
                    <span className='w-fit'>{value}</span>
                  </button>
                </div>
              )
            }
            // Если admin === 1, показываем все разделы (остальные условия ниже не трогаем)
            // "Обратная связь" всегда показываем
            if (valueTrimmed === "Обратная связь") {
              return (
                <div key={path}>
                  <button
                    onClick={() => navigateTo(path)}
                    className={`${
                      pathname === path
                        ? "text-orange [&>div]:text-orange"
                        : "hover:text-orange [&>div]:text-gray group text-white"
                    } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                  >
                    <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon
                        sx={{
                          width: "20px",
                          color:
                            pathname === path ? "secondary.main" : undefined,
                        }}
                      />
                    </div>
                    <span className='w-fit'>{value}</span>
                  </button>
                </div>
              )
            }

            // Для direction_id === 6 показываем только определённые пункты
            if (direction_id_from_user === 6) {
              const isVisibleForCategory1 = [
                "Записи",
                "Клиенты",
                "Обратная связь",
                "Настройки",
              ].includes(valueTrimmed)
              if (!isVisibleForCategory1) return null
              return (
                <div key={path}>
                  <button
                    onClick={() => navigateTo(path)}
                    className={`${
                      pathname === path
                        ? "text-orange [&>div]:text-orange"
                        : "hover:text-orange [&>div]:text-gray group text-white"
                    } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal`}
                  >
                    <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon
                        sx={{
                          width: "20px",
                          color:
                            pathname === path ? "secondary.main" : undefined,
                        }}
                      />
                    </div>
                    <span className='w-fit'>{value}</span>
                  </button>
                </div>
              )
            }
            // Скрываем пункты для ролей 2 и 6 по шагам
            const hideStepItems =
              userRoleId === 6 ||
              (userRoleId === 2 &&
                [
                  "Категории услуг",
                  "Услуги",
                  "Настройки",
                  "Сотрудники",
                  "Клиенты",
                  "Записи",
                  "Финансы",
                  "Лаборатория",
                ].includes(valueTrimmed))

            // Классы для шагов
            const stepClass =
              pathname === path || (hasSubmenu && pathname.startsWith(path))
                ? "text-orange [&>div]:text-orange"
                : step && Number(step) >= 5
                  ? "hover:text-orange [&>div]:text-gray group text-white"
                  : step === "0" && ["Настройки"].includes(valueTrimmed)
                    ? "hover:text-orange [&>div]:text-gray group text-white"
                    : step === "1" &&
                        ["Категории услуг", "Настройки"].includes(valueTrimmed)
                      ? "hover:text-orange [&>div]:text-gray group text-white"
                      : step === "2" &&
                          ["Категории услуг", "Услуги", "Настройки"].includes(
                            valueTrimmed
                          )
                        ? "hover:text-orange [&>div]:text-gray group text-white"
                        : step === "3" &&
                            [
                              "Категории услуг",
                              "Услуги",
                              "Настройки",
                              "Сотрудники",
                            ].includes(valueTrimmed)
                          ? "hover:text-orange [&>div]:text-gray group text-white"
                          : step === "4" &&
                              [
                                "Категории услуг",
                                "Услуги",
                                "Настройки",
                                "Сотрудники",
                                "Клиенты",
                                "Записи",
                                "Финансы",
                                "Лаборатория",
                                "Отзывы",
                                "Расписание",
                              ].includes(valueTrimmed)
                            ? "hover:text-orange [&>div]:text-gray group text-white"
                            : step === ""
                              ? "text-white" // Show all items normally when step is not yet initialized
                              : "text-[#9a9c9a] pointer-events-none"

            if (hideStepItems) return null

            // Разделы с подменю (Финансы, Справочники)
            if (hasSubmenu) {
              return (
                <React.Fragment key={path}>
                  <div
                    onClick={
                      stepClass.includes("pointer-events-none")
                        ? undefined
                        : (e) => toggleSubmenu(e, valueTrimmed)
                    }
                    className={`${stepClass} flex items-center w-full whitespace-nowrap mb-7 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group cursor-pointer`}
                    style={
                      stepClass.includes("pointer-events-none")
                        ? { cursor: "not-allowed" }
                        : {}
                    }
                  >
                    <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon
                        sx={{
                          width: "20px",
                          color:
                            pathname.startsWith(path)
                              ? "secondary.main"
                              : undefined,
                        }}
                      />
                    </div>
                    <p className=' w-fit pr-[10px]'>{value}</p>
                    <div className='text-white hover:text-orange'>
                      {expandedMenus[valueTrimmed] ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </div>
                  </div>
                  {expandedMenus[valueTrimmed] &&
                    !stepClass.includes("pointer-events-none") && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className='ml-10 flex flex-col mb-4'
                      >
                        {submenu.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => {
                              dispatch(setMobileMenuOpen(false))
                            }}
                            className={`${
                              pathname === subItem.path
                                ? "text-orange [&>div]:text-orange"
                                : "text-white"
                            } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group`}
                          >
                            <span className=' w-fit pr-[10px]'>
                              {subItem.value}
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                </React.Fragment>
              )
            }

            // Добавляем пункт "Лаборатория" если доступен (только после "Услуги" и с PRO)
            if (valueTrimmed === "Услуги" && shouldShowLab) {
              return (
                <React.Fragment key={path + "-services-and-lab"}>
                  {/* Обычный пункт "Услуги" */}
                  <div key={path}>
                    <button
                      onClick={() => navigateTo(path)}
                      className={`
                        ${stepClass}
                        flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal
                      `}
                    >
                      <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon
                          sx={{
                            width: "20px",
                            color:
                              pathname === path ? "secondary.main" : undefined,
                          }}
                        />
                      </div>
                      <span className='w-fit'>{value}</span>
                    </button>
                  </div>

                  {/* Пункт "Лаборатория" с подменю */}
                  <div
                    onClick={(e) => toggleSubmenu(e, "Лаборатория")}
                    className={`${
                      (pathname.includes("/laboratory") || pathname.startsWith("/templates") || pathname.startsWith("/analyzes"))
                        ? "text-orange [&>div]:text-orange"
                        : "text-white hover:text-orange [&>div]:text-gray group"
                    } flex items-center w-full whitespace-nowrap mb-6 sm:mb-7 sm:text-lg font-normal cursor-pointer`}
                  >
                    <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <AssignmentOutlinedIcon
                        sx={{
                          width: "20px",
                          color:
                            pathname.includes("/laboratory") || pathname.startsWith("/templates") || pathname.startsWith("/analyzes")
                              ? "secondary.main"
                              : undefined,
                        }}
                      />
                    </div>
                    <p className=' w-fit pr-[10px]'>Лаборатория</p>
                    <div className='text-white hover:text-orange'>
                      {expandedMenus["Лаборатория"] ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </div>
                  </div>
                  {expandedMenus["Лаборатория"] && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className='ml-10 flex flex-col mb-4'
                    >
                      <Link
                        to='/templates'
                        onClick={() => {
                          dispatch(setMobileMenuOpen(false))
                        }}
                        className={`${
                          pathname === "/templates"
                            ? "text-orange [&>div]:text-orange"
                            : "text-white"
                        } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group`}
                      >
                        Шаблоны
                      </Link>
                      <Link
                        to='/analyzes'
                        onClick={() => {
                          dispatch(setMobileMenuOpen(false))
                        }}
                        className={`${
                          pathname.includes("/analyzes")
                            ? "text-orange [&>div]:text-orange"
                            : "text-white"
                        } flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal hover:text-orange [&>div]:text-gray group`}
                      >
                        Анализы
                      </Link>
                    </div>
                  )}
                </React.Fragment>
              )
            }

            // обычный return для остальных пунктов
            return (
              <div key={path}>
                <button
                  onClick={() => navigateTo(path)}
                  className={`
                    ${stepClass}
                    flex items-center w-full whitespace-nowrap mb-3 sm:mb-7 sm:text-lg font-normal
                  `}
                >
                  <div style={{ width: "20px", marginRight: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon
                      sx={{
                        width: "20px",
                        color: pathname === path ? "secondary.main" : undefined,
                      }}
                    />
                  </div>
                  <span className='w-fit'>
                    {value}
                    {value === "Отзывы" && reviewsData?.new_reviews > 0 && (
                      <span className='ml-2 bg-orange  text-white text-xs px-3 py-[2px] rounded-full'>
                        {reviewsData?.new_reviews}
                      </span>
                    )}
                  </span>
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}